import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './CookieConsent.css';
import { 
    conditionallyInitializeAnalytics, 
    updateAnalyticsConsent, 
    removeAnalyticsCookies 
} from '../../utils/analytics';

const CookieConsent = () => {
    const [showBanner, setShowBanner] = useState(false);
    const [showPreferences, setShowPreferences] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [preferences, setPreferences] = useState({
        necessary: true, // Always true, cannot be disabled
        analytics: false,
        functional: false,
        marketing: false
    });

    useEffect(() => {
        // Initialize Google Analytics with consent mode
        conditionallyInitializeAnalytics();
        
        // Check if user has already made a choice
        const consent = localStorage.getItem('cookieConsent');
        if (!consent) {
            // Show banner after a short delay
            setTimeout(() => setShowBanner(true), 1000);
        } else {
            // Load saved preferences
            const savedPreferences = JSON.parse(consent);
            setPreferences(savedPreferences);
            updateAnalyticsConsent(savedPreferences.analytics);
        }
    }, []);

    const updateGoogleAnalytics = (analyticsEnabled) => {
        // Update Google Analytics consent mode
        updateAnalyticsConsent(analyticsEnabled);

        // Set or remove analytics cookies
        if (!analyticsEnabled) {
            removeAnalyticsCookies();
        }
    };

    const handleAcceptAll = () => {
        if (isProcessing) return; // Prevent double-clicks
        
        setIsProcessing(true);
        
        const newPreferences = {
            necessary: true,
            analytics: true,
            functional: true,
            marketing: false // We don't use marketing cookies
        };
        
        setPreferences(newPreferences);
        localStorage.setItem('cookieConsent', JSON.stringify(newPreferences));
        updateGoogleAnalytics(true);
        
        // Close immediately
        setShowBanner(false);
        setShowPreferences(false);
        setIsProcessing(false);
    };

    const handleDeclineAll = () => {
        if (isProcessing) return; // Prevent double-clicks
        
        setIsProcessing(true);
        
        const newPreferences = {
            necessary: true, // Cannot decline necessary cookies
            analytics: false,
            functional: false,
            marketing: false
        };
        
        setPreferences(newPreferences);
        localStorage.setItem('cookieConsent', JSON.stringify(newPreferences));
        updateGoogleAnalytics(false);
        
        // Close immediately
        setShowBanner(false);
        setShowPreferences(false);
        setIsProcessing(false);
    };

    const handleSavePreferences = () => {
        if (isProcessing) return; // Prevent double-clicks
        
        setIsProcessing(true);
        
        localStorage.setItem('cookieConsent', JSON.stringify(preferences));
        updateGoogleAnalytics(preferences.analytics);
        
        // Close immediately
        setShowBanner(false);
        setShowPreferences(false);
        setIsProcessing(false);
    };

    const handleManagePreferences = (e) => {
        // Prevent any default behavior and stop propagation
        e?.preventDefault();
        e?.stopPropagation();
        
        if (isProcessing) return;
        
        
        
        // Open modal immediately
        setShowPreferences(true);
    };

    const handleClosePreferences = () => {
        if (isProcessing) return; // Only prevent if processing
        
        // Close modal immediately
        setShowPreferences(false);
    };

    const handlePreferenceChange = (type, value) => {
        if (type === 'necessary' || isProcessing) return; // Cannot change necessary cookies or during processing
        setPreferences(prev => ({
            ...prev,
            [type]: value
        }));
    };

    const handleOverlayClick = (e) => {
        // Only close if clicking the overlay itself, not the modal content
        if (e.target === e.currentTarget && !isProcessing) {
            handleClosePreferences();
        }
    };

    if (!showBanner) return null;

    return (
        <>
            {/* Main Cookie Banner - Only show when modal is not open */}
            {!showPreferences && (
                <div className={`cookie-banner ${isProcessing ? 'processing' : ''}`}>
                    <div className="cookie-banner-content">
                        <div className="cookie-banner-text">
                            <h3>We value your privacy</h3>
                            <p>
                                We use cookies to enhance your browsing experience and analyze our website traffic. 
                                This helps us provide better healthcare services and improve your user experience. 
                                You can choose which cookies to accept.
                            </p>
                            <Link to="/PrivacyPolicy" className="privacy-policy-link">
                                View our Privacy Policy
                            </Link>
                        </div>
                        <div className="cookie-banner-actions">
                            <button 
                                className={`cookie-btn cookie-btn-decline ${isProcessing ? 'loading' : ''}`}
                                onClick={handleDeclineAll}
                                disabled={isProcessing}
                            >
                                Decline All
                            </button>
                            <button 
                                className={`cookie-btn cookie-btn-manage`}
                                onClick={handleManagePreferences}
                                disabled={isProcessing}
                            >
                                Manage Preferences
                            </button>
                            <button 
                                className={`cookie-btn cookie-btn-accept ${isProcessing ? 'loading' : ''}`}
                                onClick={handleAcceptAll}
                                disabled={isProcessing}
                            >
                                Accept All
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Cookie Preferences Modal - Only show when preferences are open */}
            {showPreferences && (
                <div className="cookie-modal-overlay" onClick={handleOverlayClick}>
                    <div className={`cookie-modal ${isProcessing ? 'processing' : ''}`}>
                        <div className="cookie-modal-header">
                            <h3>Cookie Preferences</h3>
                            <button 
                                className="cookie-modal-close"
                                onClick={handleClosePreferences}
                                disabled={isProcessing}
                                type="button"
                                aria-label="Close cookie preferences"
                            >
                                ×
                            </button>
                        </div>
                        
                        <div className="cookie-modal-content">
                            <p>
                                Choose which cookies you want to accept. You can change these settings at any time, 
                                but some features may not work properly if cookies are disabled.
                            </p>

                            <div className="cookie-category">
                                <div className="cookie-category-header">
                                    <h4>Necessary Cookies</h4>
                                    <div className="cookie-toggle">
                                        <input 
                                            type="checkbox" 
                                            checked={true} 
                                            disabled={true}
                                            id="necessary"
                                        />
                                        <label htmlFor="necessary" className="toggle-label disabled">
                                            <span className="toggle-slider"></span>
                                        </label>
                                        <span className="required-label">Required</span>
                                    </div>
                                </div>
                                <p>
                                    These cookies are essential for the website to function properly. 
                                    They enable basic features like page navigation and access to secure areas.
                                </p>
                            </div>

                            <div className="cookie-category">
                                <div className="cookie-category-header">
                                    <h4>Analytics Cookies</h4>
                                    <div className="cookie-toggle">
                                        <input 
                                            type="checkbox" 
                                            checked={preferences.analytics} 
                                            onChange={(e) => handlePreferenceChange('analytics', e.target.checked)}
                                            disabled={isProcessing}
                                            id="analytics"
                                        />
                                        <label htmlFor="analytics" className={`toggle-label ${isProcessing ? 'disabled' : ''}`}>
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>
                                </div>
                                <p>
                                    These cookies help us understand how visitors interact with our website 
                                    by collecting and reporting information anonymously through Google Analytics.
                                </p>
                            </div>

                            <div className="cookie-category">
                                <div className="cookie-category-header">
                                    <h4>Functional Cookies</h4>
                                    <div className="cookie-toggle">
                                        <input 
                                            type="checkbox" 
                                            checked={preferences.functional} 
                                            onChange={(e) => handlePreferenceChange('functional', e.target.checked)}
                                            disabled={isProcessing}
                                            id="functional"
                                        />
                                        <label htmlFor="functional" className={`toggle-label ${isProcessing ? 'disabled' : ''}`}>
                                            <span className="toggle-slider"></span>
                                        </label>
                                    </div>
                                </div>
                                <p>
                                    These cookies enable enhanced functionality and personalization, 
                                    such as remembering your preferences and providing customized content.
                                </p>
                            </div>

                            <div className="cookie-category-note">
                                <p>
                                    <strong>Note:</strong> We do not use marketing or advertising cookies. 
                                    Your data is never sold to third parties for marketing purposes.
                                </p>
                            </div>
                        </div>

                        <div className="cookie-modal-actions">
                            <button 
                                className={`cookie-btn cookie-btn-decline ${isProcessing ? 'loading' : ''}`}
                                onClick={handleDeclineAll}
                                disabled={isProcessing}
                            >
                                Decline All
                            </button>
                            <button 
                                className={`cookie-btn cookie-btn-save ${isProcessing ? 'loading' : ''}`}
                                onClick={handleSavePreferences}
                                disabled={isProcessing}
                            >
                                Save Preferences
                            </button>
                            <button 
                                className={`cookie-btn cookie-btn-accept ${isProcessing ? 'loading' : ''}`}
                                onClick={handleAcceptAll}
                                disabled={isProcessing}
                            >
                                Accept All
                            </button>
                        </div>

                        <div className="cookie-modal-footer">
                            <Link to="/PrivacyPolicy" className="privacy-policy-link">
                                Read our full Privacy Policy
                            </Link>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CookieConsent;